import React, { useEffect, useState } from 'react';
import { getWhatsAppTeamOverview, getWhatsAppConversations } from '../utils/api';
import { FaUsers, FaExclamationTriangle, FaTasks, FaSpinner } from 'react-icons/fa';
import './WhatsApp.css';

const WhatsAppSupervisor = ({ language = 'ar', onOpenConversation }) => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [teamOverview, setTeamOverview] = useState(null);
  const [waitingConversations, setWaitingConversations] = useState([]);
  const [riskFilter, setRiskFilter] = useState('all'); // all | critical | warning | normal
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('waiting_desc'); // waiting_desc | waiting_asc | priority_desc | unread_desc
  const [autoRefresh, setAutoRefresh] = useState(false);
  const warningMinutes = Number(teamOverview?.sla_warning_minutes || 10);
  const criticalMinutes = Number(teamOverview?.sla_critical_minutes || 60);

  const loadSupervisorData = async () => {
    try {
      setLoading(true);
      const [teamRes, waitingRes] = await Promise.allSettled([
        getWhatsAppTeamOverview(),
        getWhatsAppConversations({ waiting_for: 'agent' })
      ]);

      if (teamRes.status === 'fulfilled') {
        setTeamOverview(teamRes.value.data || null);
      } else {
        setTeamOverview(null);
      }

      if (waitingRes.status === 'fulfilled') {
        setWaitingConversations(waitingRes.value.data?.conversations || []);
      } else {
        setWaitingConversations([]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSupervisorData();
  }, []);
  useEffect(() => {
    if (!autoRefresh) return undefined;
    const timer = setInterval(() => {
      loadSupervisorData().catch(() => {});
    }, 30000);
    return () => clearInterval(timer);
  }, [autoRefresh]);
  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      await loadSupervisorData();
    } finally {
      setRefreshing(false);
    }
  };

  const getWaitingMinutes = (conv) => {
    const source = conv?.last_inbound_at || conv?.last_message_at;
    if (!source) return 0;
    return Math.max(0, Math.floor((Date.now() - new Date(source).getTime()) / 60000));
  };
  const getEscalationGroup = (conv) => {
    const waiting = getWaitingMinutes(conv);
    if (waiting >= criticalMinutes) return 'critical';
    if (waiting >= warningMinutes) return 'warning';
    return 'normal';
  };
  const getEscalationLabel = (key) => {
    if (key === 'critical') return language === 'ar' ? 'حرج' : 'Critical';
    if (key === 'warning') return language === 'ar' ? 'تنبيه' : 'Warning';
    return language === 'ar' ? 'طبيعي' : 'Normal';
  };
  const getEscalationColor = (key) => {
    if (key === 'critical') return '#b91c1c';
    if (key === 'warning') return '#b45309';
    return '#4b5563';
  };
  const formatLeadTemperature = (value) => {
    const normalized = String(value || 'low').toLowerCase();
    if (language === 'ar') {
      if (normalized === 'high' || normalized === 'hot') return 'عالية';
      if (normalized === 'medium' || normalized === 'warm') return 'متوسطة';
      return 'منخفضة';
    }
    if (normalized === 'high' || normalized === 'hot') return 'High';
    if (normalized === 'medium' || normalized === 'warm') return 'Medium';
    return 'Low';
  };
  const downloadCsv = (filename, rows) => {
    const csv = rows.map((cols) => cols.map((col) => `"${String(col ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };
  const exportHandoverCsv = () => {
    const count = waitingConversations.length;
    const confirmation = window.confirm(
      language === 'ar'
        ? `سيتم تصدير بيانات تسليم الوردية لعدد ${count} محادثة. هل تريد المتابعة؟`
        : `This will export shift handover for ${count} conversations. Continue?`
    );
    if (!confirmation) return;
    const rows = [
      [
        language === 'ar' ? 'الهاتف' : 'Phone',
        language === 'ar' ? 'مستوى الخدمة' : 'SLA level',
        language === 'ar' ? 'دقائق الانتظار' : 'Waiting minutes',
        language === 'ar' ? 'غير مقروء' : 'Unread',
        language === 'ar' ? 'جاهزية العميل' : 'Lead readiness',
        language === 'ar' ? 'الأولوية' : 'Priority',
        language === 'ar' ? 'المكلّف' : 'Assignee',
        language === 'ar' ? 'آخر رسالة' : 'Last message'
      ],
      ...waitingConversations.map((conv) => [
        conv.phone,
        getEscalationGroup(conv),
        getWaitingMinutes(conv),
        Number(conv.unread_count || 0),
        formatLeadTemperature(conv.client_readiness || conv.lead_temperature),
        Math.round(Number(conv.priority_score || 0)),
        conv.assigned_employee_name || (language === 'ar' ? 'غير مكلّف' : 'Unassigned'),
        conv.last_message || ''
      ])
    ];
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    downloadCsv(`whatsapp-supervisor-handover-${stamp}.csv`, rows);
  };

  if (loading) {
    return (
      <div className="wa-loading">
        <div className="wa-loading-spinner" />
        <p className="wa-loading-text">
          {language === 'ar' ? 'جاري تحميل بيانات الإشراف...' : 'Loading supervisor data...'}
        </p>
      </div>
    );
  }

  const highRisk = waitingConversations.filter((c) => getWaitingMinutes(c) >= criticalMinutes).length;
  const filteredWaitingConversations = waitingConversations
    .filter((c) => riskFilter === 'all' || getEscalationGroup(c) === riskFilter)
    .filter((c) => {
      const q = String(searchQuery || '').trim().toLowerCase();
      if (!q) return true;
      const phone = String(c.phone || '').toLowerCase();
      const assignee = String(c.assigned_employee_name || '').toLowerCase();
      const msg = String(c.last_message || '').toLowerCase();
      return phone.includes(q) || assignee.includes(q) || msg.includes(q);
    })
    .sort((a, b) => {
      if (sortBy === 'waiting_asc') return getWaitingMinutes(a) - getWaitingMinutes(b);
      if (sortBy === 'priority_desc') return Number(b.priority_score || 0) - Number(a.priority_score || 0);
      if (sortBy === 'unread_desc') return Number(b.unread_count || 0) - Number(a.unread_count || 0);
      return getWaitingMinutes(b) - getWaitingMinutes(a);
    });

  return (
    <div className="wa-animate-in wa-supervisor-root">
      <div className="wa-supervisor-header">
        <h2>
          <span style={{ width: '40px', height: '40px', borderRadius: '12px', background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '18px', boxShadow: '0 4px 12px rgba(59,130,246,0.35)' }}>
            <FaUsers />
          </span>
          {language === 'ar' ? 'لوحة الإشراف' : 'Supervisor Dashboard'}
        </h2>
        <p>{language === 'ar' ? 'متابعة المحادثات المنتظرة ومستوى الخدمة' : 'Monitor waiting conversations and SLA levels'}</p>
      </div>

      <div className="wa-supervisor-toolbar" style={{ marginBottom: '20px', padding: '14px 18px', background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          {[
            { id: 'all', label: language === 'ar' ? 'الكل' : 'All' },
            { id: 'critical', label: language === 'ar' ? 'حرج' : 'Critical' },
            { id: 'warning', label: language === 'ar' ? 'تنبيه' : 'Warning' },
            { id: 'normal', label: language === 'ar' ? 'طبيعي' : 'Normal' }
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => setRiskFilter(item.id)}
              className={`wa-chip ${riskFilter === item.id ? 'active' : ''}`}
              style={{ padding: '8px 14px', fontSize: '13px', fontWeight: '600', borderRadius: '8px', border: riskFilter === item.id ? '2px solid #f97316' : '1px solid #e2e8f0', background: riskFilter === item.id ? '#fff7ed' : '#fff', color: riskFilter === item.id ? '#ea580c' : '#64748b' }}
            >
              {item.label}
            </button>
          ))}
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={language === 'ar' ? 'بحث (هاتف/مكلّف/رسالة)...' : 'Search (phone/assignee/message)...'}
            className="wa-input"
            style={{ minWidth: '240px', height: '40px', padding: '0 14px', fontSize: '13px', borderRadius: '8px', border: '1px solid #e2e8f0' }}
          />
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="wa-select"
            style={{ height: '40px', minWidth: '200px', padding: '0 12px', fontSize: '13px', borderRadius: '8px', border: '1px solid #e2e8f0', fontWeight: '500' }}
          >
            <option value="waiting_desc">{language === 'ar' ? 'الفرز: الانتظار (الأعلى أولاً)' : 'Sort: Waiting (desc)'}</option>
            <option value="waiting_asc">{language === 'ar' ? 'الفرز: الانتظار (الأقل أولاً)' : 'Sort: Waiting (asc)'}</option>
            <option value="priority_desc">{language === 'ar' ? 'الفرز: الأولوية' : 'Sort: Priority'}</option>
            <option value="unread_desc">{language === 'ar' ? 'الفرز: غير المقروء' : 'Sort: Unread'}</option>
          </select>
          <button
            onClick={() => setAutoRefresh((prev) => !prev)}
            className={`wa-btn ${autoRefresh ? 'wa-btn-primary' : 'wa-btn-subtle'}`}
            style={{ height: '40px', padding: '0 16px', fontSize: '13px' }}
            title={language === 'ar' ? 'تحديث تلقائي كل 30 ثانية' : 'Auto refresh every 30s'}
          >
            {language === 'ar' ? (autoRefresh ? 'تحديث تلقائي: يعمل' : 'تحديث تلقائي') : (autoRefresh ? 'Auto: ON' : 'Auto')}
          </button>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="wa-btn wa-btn-subtle"
            style={{ height: '40px', padding: '0 16px', fontSize: '13px' }}
          >
            {refreshing ? (language === 'ar' ? 'جاري التحديث...' : 'Refreshing...') : (language === 'ar' ? 'تحديث' : 'Refresh')}
          </button>
          <button
            onClick={exportHandoverCsv}
            className="wa-btn wa-btn-subtle"
            style={{ height: '40px', padding: '0 16px', fontSize: '13px' }}
            title={language === 'ar' ? 'تصدير بيانات تسليم الوردية' : 'Export shift handover'}
          >
            {language === 'ar' ? 'تصدير بيانات' : 'Export Handover'}
          </button>
        </div>
      </div>

      <div className="wa-supervisor-metrics" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        <div
          className="wa-supervisor-metric-card"
          role="button"
          tabIndex={0}
          onClick={() => setRiskFilter('all')}
          onKeyDown={(e) => { if (e.key === 'Enter') setRiskFilter('all'); }}
          style={{ cursor: 'pointer', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0', background: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', transition: 'all 0.2s ease' }}
          onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 4px 14px rgba(0,0,0,0.08)'; e.currentTarget.style.borderColor = '#f97316'; }}
          onMouseLeave={(e) => { e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#475569', fontSize: '13px', fontWeight: '600', marginBottom: '12px' }}>
            <FaTasks style={{ fontSize: '18px', color: '#64748b' }} /> {language === 'ar' ? 'المحادثات المنتظرة' : 'Waiting Conversations'}
          </div>
          <div className="wa-metric-value" style={{ fontSize: '32px', fontWeight: '700', color: '#0f172a' }}>{teamOverview?.total_waiting || waitingConversations.length}</div>
        </div>
        <div
          className="wa-supervisor-metric-card"
          role="button"
          tabIndex={0}
          onClick={() => setRiskFilter('warning')}
          onKeyDown={(e) => { if (e.key === 'Enter') setRiskFilter('warning'); }}
          style={{ cursor: 'pointer', padding: '20px', borderRadius: '12px', border: '1px solid #fde68a', background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)', boxShadow: '0 2px 8px rgba(251,191,36,0.15)', transition: 'all 0.2s ease' }}
          onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 4px 14px rgba(251,191,36,0.25)'; e.currentTarget.style.borderColor = '#f59e0b'; }}
          onMouseLeave={(e) => { e.currentTarget.style.boxShadow = '0 2px 8px rgba(251,191,36,0.15)'; e.currentTarget.style.borderColor = '#fde68a'; }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#92400e', fontSize: '13px', fontWeight: '600', marginBottom: '12px' }}>
            <FaExclamationTriangle style={{ fontSize: '18px' }} /> {language === 'ar' ? `تنبيه مستوى الخدمة (${warningMinutes}+ د)` : `SLA Warnings (${warningMinutes}+ min)`}
          </div>
          <div className="wa-metric-value" style={{ fontSize: '32px', fontWeight: '700', color: '#b45309' }}>{teamOverview?.breached_warning || 0}</div>
        </div>
        <div
          className="wa-supervisor-metric-card"
          role="button"
          tabIndex={0}
          onClick={() => setRiskFilter('critical')}
          onKeyDown={(e) => { if (e.key === 'Enter') setRiskFilter('critical'); }}
          style={{ cursor: 'pointer', padding: '20px', borderRadius: '12px', border: '1px solid #fecaca', background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)', boxShadow: '0 2px 8px rgba(220,38,38,0.12)', transition: 'all 0.2s ease' }}
          onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 4px 14px rgba(220,38,38,0.2)'; e.currentTarget.style.borderColor = '#dc2626'; }}
          onMouseLeave={(e) => { e.currentTarget.style.boxShadow = '0 2px 8px rgba(220,38,38,0.12)'; e.currentTarget.style.borderColor = '#fecaca'; }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#b91c1c', fontSize: '13px', fontWeight: '600', marginBottom: '12px' }}>
            <FaUsers style={{ fontSize: '18px' }} /> {language === 'ar' ? `حرج مستوى الخدمة (${criticalMinutes}+ د)` : `SLA Critical (${criticalMinutes}+ min)`}
          </div>
          <div className="wa-metric-value" style={{ fontSize: '32px', fontWeight: '700', color: '#dc2626' }}>{teamOverview?.breached_critical || highRisk}</div>
        </div>
      </div>

      <div className="wa-supervisor-queue">
        <div className="wa-supervisor-queue-header">
          <span className="wa-supervisor-queue-title">
            {language === 'ar' ? 'أعلى المخاطر (بانتظار الموظف)' : 'High Risk Queue (Waiting for Employee)'}
          </span>
          <span className="wa-supervisor-queue-count">
            {language === 'ar' ? 'النتائج' : 'Results'}: {filteredWaitingConversations.length}
          </span>
        </div>
        <div style={{ padding: '4px 0' }}>
        {filteredWaitingConversations.slice(0, 16).map((conv) => {
          const group = getEscalationGroup(conv);
          const unread = Number(conv.unread_count || 0);
          return (
          <div
            key={conv.id}
            className="wa-supervisor-row"
            role={onOpenConversation ? 'button' : undefined}
            tabIndex={onOpenConversation ? 0 : undefined}
            onClick={onOpenConversation ? () => onOpenConversation(conv.id) : undefined}
            onKeyDown={onOpenConversation ? (e) => { if (e.key === 'Enter') onOpenConversation(conv.id); } : undefined}
            style={{
              cursor: onOpenConversation ? 'pointer' : undefined,
              padding: '14px 20px',
              margin: '0 8px',
              borderRadius: '8px',
              border: '1px solid transparent',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              if (onOpenConversation) {
                e.currentTarget.style.background = '#f8fafc';
                e.currentTarget.style.borderColor = '#e2e8f0';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.borderColor = 'transparent';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                <span style={{ color: '#111827', fontWeight: 600, fontSize: '14px' }}>{conv.phone}</span>
                {unread > 0 && (
                  <span style={{ fontSize: '11px', fontWeight: 700, color: '#fff', backgroundColor: '#ea580c', borderRadius: '999px', padding: '2px 6px', minWidth: '20px', textAlign: 'center' }}>
                    {unread}
                  </span>
                )}
              </div>
              <span style={{ fontSize: '12px', color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '380px' }}>
                {conv.last_message || (language === 'ar' ? 'لا توجد رسالة' : 'No message')}
              </span>
              {conv.assigned_employee_name && (
                <span style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>
                  {language === 'ar' ? 'المكلّف:' : 'Assignee:'} {conv.assigned_employee_name}
                </span>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
              <span style={{ fontSize: '11px', fontWeight: 700, color: '#fff', backgroundColor: getEscalationColor(group), borderRadius: '8px', padding: '4px 8px' }}>
                {getEscalationLabel(group)}
              </span>
              <span style={{ fontSize: '13px', color: getWaitingMinutes(conv) >= criticalMinutes ? '#b91c1c' : getWaitingMinutes(conv) >= warningMinutes ? '#b45309' : '#6b7280', fontWeight: 700, minWidth: '36px', textAlign: 'right' }}>
                {getWaitingMinutes(conv)}m
              </span>
            </div>
          </div>
        );
      })}
        {filteredWaitingConversations.length === 0 && (
          <div style={{ padding: '48px 24px', textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.4 }}>✓</div>
            <div style={{ fontSize: '15px', fontWeight: '600', color: '#0f172a', marginBottom: '8px' }}>
              {language === 'ar' ? 'لا توجد محادثات منتظرة' : 'No waiting conversations'}
            </div>
            <div style={{ fontSize: '13px', color: '#64748b' }}>
              {language === 'ar' ? 'جميع المحادثات تلقى متابعة من الموظفين' : 'All conversations are being handled by the team'}
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default WhatsAppSupervisor;
